import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@/components/Tooltip';
import AdTypeBadge from '@/components/AdTypeBadge';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { RateUpdatedLabel } from '@/components/RateUpdatedLabel';
import { useAppSelector } from '@/store/hooks';
import { useI18nText } from '@/lib/utils/i18nText';
import { EmptyDash, formatNum, getProposedPrice, getPpvEstimate } from '../../utils';
import { CAMPAIGN_INFLUENCER_STATUSES } from '../../constants';
import { ceilWithCommission } from '@/lib/utils/misc';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcTag } from '@/components/ui/ggc/tag';
import { RowActionButton } from '@/pages/influencers/components/RowActionButton';
import { GgcSelect } from '@/components/ui/ggc/select';
import { savePanelOverview } from '@/lib/api/panel.api';
import { Edit3, Plus, Trash2, Save, X } from 'lucide-react';
import type { Influencer, Campaign } from '../../types';
import {
  ADV_TYPE_LABELS,
  ADV_TYPE_OPTIONS,
  ADV_TYPES_WITH_VARIATION,
  getAdvTypePriority,
  type RateEntry,
} from '@/lib/constants/advTypes';

type RawRatesV2Item = Influencer['ratesV2'] extends { items?: (infer I)[] } | undefined ? NonNullable<I> : never;

function getItemVariation(item: RawRatesV2Item): number | string | null {
  const vc = ADV_TYPES_WITH_VARIATION[item.type ?? ''];
  if (!vc) return null;
  const raw = item as Record<string, unknown>;
  if (raw['streamOnly']) return (raw['streamOnly'] as { hours?: number }).hours ?? vc.default;
  if (raw['footageRightOnly']) return (raw['footageRightOnly'] as { duration?: string }).duration ?? vc.default;
  if (raw['tiktokLiveOnly']) return (raw['tiktokLiveOnly'] as { hours?: number }).hours ?? vc.default;
  return vc.default;
}

function itemsToEntries(items: RawRatesV2Item[]): RateEntry[] {
  return items.map((item) => ({
    type: item.type ?? '',
    price: String(item.price ?? ''),
    variation: getItemVariation(item),
    updatedAt: item.updatedAt ?? null,
  }));
}

/** Human-readable duration label for a rate's variation (e.g. "2h", "3 months"). */
function formatVariationLabel(type: string, variation: number | string | null): string | null {
  if (variation == null) return null;
  const vc = ADV_TYPES_WITH_VARIATION[type];
  const opt = vc?.options.find((o) => String(o.value) === String(variation));
  return opt?.text ?? String(variation);
}

type PriceBreakdownItem = NonNullable<
  NonNullable<Influencer['campaigns']>[number]['priceBreakDown']
> extends (infer I)[] ? I : never;

/** Per-video-type price chips under a Final offer / Advertiser price cell — mirrors
 *  InfluencerRow.tsx's `renderBreakdownChips` (not exported, so re-implemented here)
 *  for campaigns negotiated with a per-type breakdown instead of one flat total. */
function renderPriceBreakdownChips(breakdown: PriceBreakdownItem[] | undefined, mode: 'final' | 'advertiser') {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="est-price-breakdown-chips">
      {breakdown.map((b, i) => {
        const label = ADV_TYPE_LABELS[b.partnership ?? ''] ?? b.partnership ?? '';
        const price = mode === 'final' ? b.priceFinal : b.priceAdvertiser;
        return <span key={i} className="breakdown-chip">{label}, ${formatNum(price ?? 0)}</span>;
      })}
    </div>
  );
}

/* ── cell helpers ── */

function getFirstRate(items: RawRatesV2Item[]): { type?: string; price?: number; adTypeLabel?: string } | null {
  if (!items.length) return null;
  const sorted = [...items]
    .filter((i) => i.price != null)
    .sort((a, b) => {
      const da = new Date(a.updatedAt || 0).getTime();
      const db = new Date(b.updatedAt || 0).getTime();
      if (db !== da) return db - da;
      return (b.price ?? 0) - (a.price ?? 0);
    });
  const first = sorted[0];
  if (!first) return null;
  const label = ADV_TYPE_LABELS[first.type ?? ''] ?? first.type ?? 'Rate';
  return { type: first.type, price: first.price, adTypeLabel: label };
}

/* ── component ── */

export function EstPriceCell({ user, campaignsById, currentCampaign, organizationId, showEmptyDash = true }: {
  user: Influencer;
  campaignsById: Map<string, Campaign>;
  currentCampaign?: Campaign | null;
  organizationId?: string;
  showEmptyDash?: boolean;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [placeAbove, setPlaceAbove] = useState(true);
  // Clamped to whichever side (above/below) the popover lands on has room for —
  // .est-price-popover's own max-height is just an upper bound, so a popover
  // near the top/bottom edge of the scroll boundary would otherwise get
  // silently clipped by that same boundary instead of shrinking to fit.
  const [maxPopoverHeight, setMaxPopoverHeight] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [editEntries, setEditEntries] = useState<RateEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedItems, setSavedItems] = useState<RawRatesV2Item[] | null>(null);
  // cellRef: outside-click boundary — the whole cell (summary rows + trigger), so
  // clicking the price summary text doesn't count as "outside" and close the popover.
  const cellRef = useRef<HTMLDivElement>(null);
  // triggerRef: wraps just the Details button — the popover's `position:relative`
  // anchor and the rect used for above/below placement. It used to be `cellRef`,
  // but `.est-price-cell`'s own height also includes the summary rows *above* the
  // button (grid row 1), so `bottom: calc(100% + 8px)` measured a gap above the
  // whole cell instead of just above the button — a large, wrong gap whenever a
  // cell had summary rows to show and the popover opened upward.
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (cellRef.current && !cellRef.current.contains(e.target as Node)) {
        setOpen(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Measure against the nearest clipping ancestor (e.g. a `ggc-table-frame`
    // with overflow-y: hidden/auto), not just the viewport — otherwise a
    // popover can be placed "below" because the viewport has room, then get
    // silently clipped by a scroll container that ends right at the trigger.
    let boundaryTop = 0;
    let boundaryBottom = window.innerHeight;
    let node: HTMLElement | null = triggerRef.current.parentElement;
    while (node) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|hidden)/.test(style.overflowY)) {
        const boundsRect = node.getBoundingClientRect();
        boundaryTop = boundsRect.top;
        boundaryBottom = boundsRect.bottom;
        break;
      }
      node = node.parentElement;
    }
    const spaceBelow = boundaryBottom - rect.bottom;
    const spaceAbove = rect.top - boundaryTop;
    setPlaceAbove(spaceBelow < spaceAbove);
    // Shrink to whichever side actually has room — .est-price-popover's CSS
    // max-height (520px) is just the comfortable ceiling, not a guarantee.
    const available = Math.max(spaceBelow, spaceAbove) - 16;
    setMaxPopoverHeight(Math.max(240, Math.min(520, available)));
  }, [open]);

  // ratesV2 없으면 rates에서 변환
  const baseRatesItems = useMemo<RawRatesV2Item[]>(() => {
    const src = savedItems ?? user.ratesV2?.items;
    if (src?.length) return src as RawRatesV2Item[];
    const oldRates = user.rates;
    if (!oldRates?.data) return [];
    const items: RawRatesV2Item[] = [];
    for (const [advType, price] of Object.entries(oldRates.data)) {
      if (typeof price !== 'number' || price <= 0) continue;
      items.push({ type: advType, price, updatedAt: oldRates.updateDates?.[advType] || oldRates.updatedAt } as RawRatesV2Item);
    }
    return items;
  }, [savedItems, user.ratesV2, user.rates]);

  const ratesItems = useMemo(() => {
    return [...baseRatesItems]
      .filter((i) => i.price != null)
      .sort((a, b) => {
        const da = new Date(b.updatedAt || 0).getTime();
        const db = new Date(a.updatedAt || 0).getTime();
        return da - db || (b.price ?? 0) - (a.price ?? 0);
      });
  }, [baseRatesItems]);

  const proposedPrice = getProposedPrice(user, organizationId);
  const firstRate = getFirstRate(ratesItems);
  const ppvEstimate = getPpvEstimate(user, currentCampaign);

  // Popover header — whose price info this is. Matches the name shown in the row itself
  // (InfluencerRow's own `name` fallback chain), so the popover reads as belonging to it
  // even once it's floated away from the trigger.
  const ch = user.channels?.[0];
  const displayName = user.profile?.fullname || user.profile?.name || ch?.title || t('Unknown', '알 수 없음');

  const userCampaigns = useMemo(() => {
    const statusWeight: Record<string, number> = {
      completed: 1, in_progress: 2, waiting_for_payment: 3, payment_done: 4,
      content_creation: 5, negotiation: 6, invited: 7, contacting: 8,
      prospect: 9, candidate: 10, incomplete: 90, rejected: 91, deleted: 92,
    };
    const raw = Array.isArray(user.campaigns) ? user.campaigns : [];
    const currentCampId = currentCampaign?._id ?? currentCampaign?.id;
    return raw
      .filter((uc) => {
        if (currentCampId && uc.campaignId === currentCampId) return false;
        const campaign = uc.campaignId ? campaignsById.get(uc.campaignId) : null;
        if (!campaign?.name) return false;
        return true;
      })
      .sort((a, b) => (statusWeight[a.status ?? ''] ?? 50) - (statusWeight[b.status ?? ''] ?? 50) || (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
      .slice(0, 20);
  }, [user.campaigns, campaignsById, currentCampaign]);

  // Mirrors the exact three summary rows rendered below (proposedPrice / firstRate / ppvEstimate)
  // — the dash shows if and only if none of them render, regardless of campaign membership.
  // Campaign membership alone (e.g. a proper candidate with no rate card, no proposed price, and
  // no PPV estimate) used to suppress the dash on the theory that the Campaigns history popover
  // has something useful to show instead — but that leaves the collapsed row's summary area
  // visibly blank above the Details button, which reads as broken exactly like the empty-cell
  // case this dash exists to fix in the first place. Show it whenever there's genuinely nothing
  // in the row summary, independent of what the popover's history section might contain.
  const hasAnyMainRow = proposedPrice != null || firstRate != null || (ppvEstimate != null && !!currentCampaign);
  // Only gates the top-of-cell summary rows (EmptyDash below) — it must NOT disable the
  // Details button itself. A candidate with no campaign, no rate, and no proposed price is
  // exactly the case an admin needs to pre-fill a first price for (see startEdit()'s empty-state
  // pre-fill below), and the BE save endpoint has no campaign/rate prerequisite of its own.
  const hasNothingToShow = !hasAnyMainRow;

  // Editing rates requires `toAccessAllFeatures` — the BE (saveOverview.action) silently skips
  // rebuilding ratesV2 for admins without it (still returns success), so gate the Edit entry
  // point here instead of letting it look like a successful save that never persisted.
  const canEditRates = useAppSelector(
    (s) => !!(s.envs.currentUser as { permissions?: Record<string, boolean> } | null)?.permissions?.toAccessAllFeatures,
  );
  const editDisabledReason = canEditRates ? null : t(
    "You don't have permission to edit channel rates.",
    '채널 가격을 편집할 권한이 없습니다.',
  );

  /* ── edit handlers ── */
  // Type-level: skip types already present and move to the next priority type.
  // Variation types are added with their default option (e.g. Stream 2h).
  const nextEditType = (current: RateEntry[]) => {
    const usedTypes = new Set(current.map((r) => r.type));
    return getAdvTypePriority(ch?.type).find((key) => !usedTypes.has(key));
  };

  const startEdit = () => {
    const initial = itemsToEntries(ratesItems);
    // No rates yet — pre-fill the first row (channel-appropriate ad type) so the user can type
    // a price immediately instead of also having to click "Add rate" first.
    if (initial.length === 0) {
      const next = nextEditType(initial);
      if (next) {
        const vc = ADV_TYPES_WITH_VARIATION[next];
        initial.push({ type: next, price: '', variation: vc ? vc.default : null, updatedAt: null });
      }
    }
    setEditEntries(initial);
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const addEditEntry = () => {
    const next = nextEditType(editEntries);
    if (!next) return;
    const vc = ADV_TYPES_WITH_VARIATION[next];
    setEditEntries((p) => [...p, { type: next, price: '', variation: vc ? vc.default : null, updatedAt: null }]);
  };

  const removeEditEntry = (i: number) => setEditEntries((p) => p.filter((_, idx) => idx !== i));

  const updateEditEntry = (i: number, field: 'type' | 'price' | 'variation', value: string | number) => {
    setEditEntries((p) => p.map((r, idx) => {
      if (idx !== i) return r;
      if (field === 'type') {
        const vc = ADV_TYPES_WITH_VARIATION[value as string];
        return { ...r, type: value as string, variation: vc ? vc.default : null, updatedAt: null };
      }
      return { ...r, [field]: value };
    }));
  };

  const handleSave = async () => {
    const payload = editEntries
      .filter((r) => r.type && r.price !== '')
      .map((r) => {
        const vc = ADV_TYPES_WITH_VARIATION[r.type];
        const entry: Record<string, unknown> = { advType: r.type, price: parseFloat(r.price) };
        if (vc && r.variation != null) entry[vc.name] = r.variation;
        return entry;
      });
    setSaving(true);
    try {
      // Always resend the current demographic snapshot — saveOverview rewrites demographic
      // on every call, so omitting it here would silently wipe any existing gender/age/geo/
      // screenshot data (same footgun `persistOverview` in panel/user.tsx guards against).
      await savePanelOverview({ userId: user._id, data: { demographic: user.demographic, rates: payload } });
      // Optimistically update local display
      const newItems = editEntries
        .filter((r) => r.type && r.price !== '')
        .map((r) => ({ type: r.type, price: parseFloat(r.price), updatedAt: new Date().toISOString() } as RawRatesV2Item));
      setSavedItems(newItems);
      setEditing(false);
    } catch { /* global error handler */ } finally {
      setSaving(false);
    }
  };

  /* ── render ── */
  return (
    <div className="est-price-cell" ref={cellRef}>
      {/* Main cell display */}
      <div className="est-price-main">
        {hasNothingToShow && showEmptyDash && <EmptyDash />}
        {proposedPrice != null && (
          <div className="est-price-row">
            {/* essential: once the column is narrow enough to truncate the label
                to just the icon, the Tooltip becomes the only way to tell this
                price type apart from Rate/PPV below — a real mix-up risk, not
                just a tutorial hint. */}
            <Tooltip essential content={t('Last proposed price', '최근 제안 가격')}>
              <span className="est-price-label">
                <i className="fa fa-handshake-o est-price-label-icon" aria-hidden />
                <span className="est-price-label-text">{t('Last proposed price', '최근 제안 가격')}</span>
              </span>
            </Tooltip>
            <span className="est-price-value">${formatNum(proposedPrice)}</span>
          </div>
        )}
        {firstRate != null && (
          <div className="est-price-row">
            <Tooltip essential content={`${t('Rate for', 'Rate for')} ${firstRate.adTypeLabel ?? t('rate', '단가')}`}>
              <span className="est-price-label">
                <i className="fa fa-money est-price-label-icon" aria-hidden />
                <span className="est-price-label-text">{t('Rate for', 'Rate for')} {firstRate.adTypeLabel ?? t('rate', '단가')}</span>
              </span>
            </Tooltip>
            <span className="est-price-value">${formatNum(firstRate.price)}</span>
          </div>
        )}
        {ppvEstimate != null && currentCampaign && (
          <div className="est-price-row">
            <Tooltip essential content={`${t('Est. by PPV', 'PPV 추정가')} (${currentCampaign.expectedPricePerView}/view)`}>
              <span className="est-price-label">
                <i className="fa fa-eye est-price-label-icon" aria-hidden />
                <span className="est-price-label-text">{t('Est. by PPV', 'PPV 추정가')} ({currentCampaign.expectedPricePerView}/view)</span>
              </span>
            </Tooltip>
            <span className="est-price-value">${formatNum(ppvEstimate)}</span>
          </div>
        )}
      </div>

      {/* Wraps just the Details button — the popover's own position:relative anchor,
          kept separate from .est-price-cell so `bottom/top: calc(100% + ...)` measures
          against the button's height, not the whole cell's (which also includes the
          summary rows above it). */}
      <div className="est-price-trigger" ref={triggerRef}>
        {/* Always rendered and always enabled — even with nothing to show yet, this is the entry
            point for pre-filling a first channel rate before the influencer is in any campaign. */}
        <RowActionButton
          className="mt-1 w-[60px] est-price-details-btn"
          onClick={() => { setOpen((o) => !o); if (open) setEditing(false); }}
        >
          {t('Details', '상세보기')}
        </RowActionButton>
        {open && (
          <div
            className={`est-price-popover est-price-popover--${placeAbove ? 'above' : 'below'}`}
            style={maxPopoverHeight != null ? { maxHeight: maxPopoverHeight } : undefined}
          >
            {/* Whose popover this is — replaces the old pointer-arrow indicator, which pointed
                at a fixed offset regardless of where the trigger actually sat and didn't say
                anything once the popover had floated away from the row. Thumbnail + platform
                icon match the row's own name-cell treatment so the popover is recognizable as
                the same channel even once it's floated away from the trigger. */}
            <div className="est-price-popover-header">
              <ChannelThumbnail src={ch?.thumbnail} />
              <ChannelTypeIcon type={ch?.type ?? 'youtube'} size={14} asBadge />
              <span className="est-price-popover-name">{displayName}</span>
              <button
                type="button"
                className="est-price-popover-close"
                onClick={() => { setOpen(false); setEditing(false); }}
                aria-label={t('Close', '닫기')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

          <div className="est-price-popover-body">
            {/* ── Channel Rates section ── */}
            <div className="est-price-popover-section">
              <div className="est-price-popover-section-head">
                <h4 className="ggc-text-section-header">{t('Channel Rates', '채널 가격')}</h4>
                {!editing && (
                  <Tooltip content={editDisabledReason}>
                    <GgcButton
                      type="button"
                      size='sm'
                      variant='secondary'
                      onClick={startEdit}
                      disabled={!canEditRates}
                    >
                      <Edit3 className="h-3 w-3" />
                      {t('Edit', '편집')}
                    </GgcButton>
                  </Tooltip>
                )}
              </div>
              <div className="est-price-popover-section-body">
                {editing ? (
                  <div>
                    <div className="space-y-1 mb-2">
                      {editEntries.map((r, i) => {
                        const vc = ADV_TYPES_WITH_VARIATION[r.type];
                        return (
                          <div key={i} className="flex items-center gap-1.5">
                            <GgcSelect
                              wrapperClassName="w-[200px]"
                              value={r.type}
                              onChange={(ev) => updateEditEntry(i, 'type', ev.target.value)}
                            >
                              {ADV_TYPE_OPTIONS.map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </GgcSelect>
                            {vc && (
                              <GgcSelect
                                wrapperClassName="w-[80px]"
                                value={r.variation ?? vc.default}
                                onChange={(ev) => {
                                  const val = typeof vc.options[0]?.value === 'number' ? Number(ev.target.value) : ev.target.value;
                                  updateEditEntry(i, 'variation', val);
                                }}
                              >
                                {vc.options.map((op) => (
                                  <option key={String(op.value)} value={op.value}>{op.text}</option>
                                ))}
                              </GgcSelect>
                            )}
                            <div className="flex items-center rounded-ggc-md border border-ggc-border-gray-dark bg-white h-[38px]">
                              <span className="pl-1.5 text-xs text-ggc-text-gray">$</span>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-24 !border-none py-1 pr-1.5 text-right text-xs focus:outline-none"
                                value={r.price}
                                onChange={(ev) => updateEditEntry(i, 'price', ev.target.value)}
                                placeholder="0"
                              />
                            </div>
                            <RateUpdatedLabel updatedAt={r.updatedAt} />
                            <button onClick={() => removeEditEntry(i)} className="text-ggc-red hover:opacity-70 shrink-0">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={addEditEntry}
                      className="mb-2 w-full rounded border border-dashed border-ggc-border-gray py-1 text-[11px] text-ggc-disabled hover:border-ggc-border-gray-dark hover:text-ggc-text-gray transition-colors"
                    >
                      <Plus className="inline h-3 w-3 mr-0.5" />{t('Add rate', '가격 추가')}
                    </button>
                    <div className="flex gap-1.5">
                      <GgcButton variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
                        <X className="h-3 w-3" />{t('Cancel', '취소')}
                      </GgcButton>
                      <GgcButton variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                        <Save className="h-3 w-3" />{saving ? t('Saving...', '저장 중...') : t('Save', '저장')}
                      </GgcButton>
                    </div>
                  </div>
                ) : (
                  ratesItems.length > 0 ? (
                    <div className="ggc-table-frame">
                      <table className="gc-table gc-table--compact">
                        <thead>
                          <tr>
                            <th>{t('Ad Type', '광고 유형')}</th>
                            <th className="text-right">{t('Price', '가격')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ratesItems.map((r, i) => {
                            const durationLabel = formatVariationLabel(r.type ?? '', getItemVariation(r));
                            return (
                              <tr key={`rate-${i}`}>
                                <td>
                                  <Tooltip content={`${t('Rate for', '단가')} ${r.type ?? 'Unknown'}`}>
                                    <AdTypeBadge typeId={r.type} label={ADV_TYPE_LABELS[r.type ?? ''] ?? r.type ?? 'Rate'} />
                                  </Tooltip>
                                  {durationLabel && <GgcTag className="ml-1.5">{durationLabel}</GgcTag>}
                                </td>
                                <td className="text-right">${formatNum(r.price)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-[11px] text-ggc-disabled italic">{t('No rates set', '가격 미설정')}</p>
                  )
                )}
              </div>
            </div>

            {/* ── Campaigns history section ── */}
            {userCampaigns.length > 0 && (
              <div className="est-price-popover-section">
                <div className="est-price-popover-section-head">
                  <h4 className="ggc-text-section-header">{t('Campaign Final offer / price history', '캠페인 Final offer / price 히스토리')}</h4>
                </div>
                <div className="est-price-popover-section-body">
                  <div className="ggc-table-frame">
                    <table className="gc-table gc-table--compact">
                      <thead>
                        <tr>
                          <th className="text-center w-[32px]">{t('Status', '상태')}</th>
                          <th>{t('Campaign', '캠페인')}</th>
                          <th className="text-right">{t('Final offer', '최종 제안가')}</th>
                          <th className="text-right">{t('Advertiser price', '광고주 지불가')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userCampaigns.map((uc, i) => {
                          const campaign = uc.campaignId ? campaignsById.get(uc.campaignId) : null;
                          const priceFinal = uc.finalPrice ?? uc.priceFinal;
                          // Advertiser price defaults to final price × commission rate when not
                          // explicitly stored — mirrors InfluencerRow.tsx's 'advertiserPrice' column.
                          const advertiserPriceStored = uc.priceAdvertiser ?? uc.advertiserPrice;
                          const commissionRate = campaign?.commissionRate ?? 0;
                          const advertiserPriceComputed = commissionRate > 0 && priceFinal
                            ? ceilWithCommission(priceFinal, commissionRate)
                            : undefined;
                          const advertiserPrice = advertiserPriceStored ?? advertiserPriceComputed;
                          const statusInfo = CAMPAIGN_INFLUENCER_STATUSES.find((s) => s.value === uc.status) ?? { icon: 'circle-o', text: '' };
                          const isIgnored = uc.status === 'deleted' || uc.status === 'incomplete' || uc.status === 'rejected';
                          // `candidateManualEstimation.partnership` is the field that's actually populated
                          // (stream/vod/full/pre-roll, keyed into ADV_TYPE_LABELS) — the older `videoType`
                          // field is empty in practice, kept only as a defensive fallback.
                          const videoType = uc.candidateManualEstimation?.partnership ?? uc.videoType;
                          const videoTypeLabel = videoType ? (ADV_TYPE_LABELS[videoType] ?? videoType) : null;
                          return (
                            <tr key={i} className={isIgnored ? 'est-price-table-row--ignored' : undefined}>
                              <td className="text-center">
                                <Tooltip content={statusInfo.text || uc.status || 'Unknown'}>
                                  <span className="inline-flex justify-center">
                                    <i className={`fa fa-${statusInfo.icon}`} style={{ color: isIgnored ? 'var(--ggc-disabled)' : '#4caf50' }} />
                                  </span>
                                </Tooltip>
                              </td>
                              <td>
                                {uc.campaignId ? (
                                  <Link to={`/dashboard/campaign/${encodeURIComponent(uc.campaignId)}`} className="influencer-name-link">
                                    {campaign?.name ?? 'Unknown'}
                                  </Link>
                                ) : (campaign?.name ?? 'Unknown')}
                              </td>
                              <td className="text-right">
                                {priceFinal != null ? `$${formatNum(priceFinal)}` : '-'}
                                {videoTypeLabel && (
                                  <Tooltip content={t('Video type', '영상 유형')}>
                                    <span className="est-price-table-subline">{videoTypeLabel}</span>
                                  </Tooltip>
                                )}
                                {renderPriceBreakdownChips(uc.priceBreakDown, 'final')}
                              </td>
                              <td className="text-right">
                                {advertiserPrice != null ? `$${formatNum(advertiserPrice)}` : '-'}
                                {/* Only when computed (not a BE-stored value) — shows the exact math
                                    behind the fallback, mirroring InfluencerRow.tsx's 'advertiserPrice'
                                    column caption. */}
                                {advertiserPriceStored == null && advertiserPriceComputed != null && (
                                  <span className="est-price-table-subline">
                                    ${formatNum(priceFinal)} × {commissionRate.toFixed(2)} = ${formatNum(advertiserPriceComputed)}
                                  </span>
                                )}
                                {renderPriceBreakdownChips(uc.priceBreakDown, 'advertiser')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
